<template>
  <div class="flex justify-between gap-8">
    <div
      v-if="selectedOffer.merchant"
      class="text-static-default-low text-caption mb-16"
      :class="{ 'flex flex-wrap': shouldDisplaySellerFlag }"
    >
      <FormattedMessage
        :definition="
          category !== 'Refurbished'
            ? translations.resultSellerNew
            : translations.resultSeller
        "
        tag="span"
      >
        <template #seller>
          <RevLink
            class="truncate"
            :to="selectedOffer.merchant.merchantUrl"
            @click="onSellerClick"
          >
            {{ selectedOffer.merchant.company }}
          </RevLink>
        </template>
      </FormattedMessage>

      <Flag
        v-if="shouldDisplaySellerFlag"
        :aria-label="selectedOffer.merchant.country.name"
        class="ml-8 shrink-0"
        :country-code="selectedOffer.merchant.country.code as MarketCountryCode"
      />

      <span v-if="shouldDisplaySellerCountry" class="ml-4 shrink-0">
        ({{ selectedOffer.merchant.country.name }})
      </span>
    </div>
    <RevLink
      v-if="marketplace.marketplace.marketplaceCode === MarketplaceCode.EU"
      class="text-action-default-hi caption-link h-fit shrink-0 pt-4"
      @click="openModal(MODAL_NAMES.SELLER_DETAILS)"
    >
      {{ i18n(translations.sellerDetails) }}
    </RevLink>
  </div>
  <SellerDetailsModal
    v-if="marketplace.marketplace.marketplaceCode === MarketplaceCode.EU"
    :listing-id="selectedOffer?.offerId"
  />
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { MarketCountryCode, MarketplaceCode } from '@backmarket/http-api'
import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevLink } from '@ds/components/Link'
import { openModal } from '@ds/components/ModalBase'

import { MODAL_NAMES } from '~/scopes/product/constants'

import translations from './Seller.translations'
import SellerDetailsModal from './components/SellerDetailsModal/SellerDetailsModal.vue'

const COUNTRIES_WITH_SELLER_FLAG = [MarketCountryCode.US, MarketCountryCode.AU]

const i18n = useI18n()
const { trackClick } = useTracking()
const marketplace = useMarketplace()
const experiments = useExperiments()

const props = defineProps<{
  selectedOffer: NonNullable<GetPickersResponse['selectedOffer']>
  category: GetProductResponse['type']
}>()

const shouldDisplaySellerFlag = computed(() => {
  if (experiments['experiment.ppSellerFlag'] === 'withSellerFlag') {
    return (
      !!props.selectedOffer.merchant &&
      !!props.selectedOffer.merchant.country.code &&
      props.selectedOffer.merchant.country.code ===
        marketplace.market.countryCode
    )
  }

  return (
    COUNTRIES_WITH_SELLER_FLAG.includes(marketplace.market.countryCode) &&
    !!props.selectedOffer.merchant &&
    !!props.selectedOffer.merchant.country.code
  )
})

const shouldDisplaySellerCountry = computed(() => {
  return (
    !!props.selectedOffer.merchant &&
    !!props.selectedOffer.merchant.country.code &&
    !shouldDisplaySellerFlag.value
  )
})

function onSellerClick() {
  trackClick({
    zone: 'recap',
    name: 'seller',
    value:
      props.selectedOffer.merchant?.country.code ||
      props.selectedOffer.merchant?.country ||
      '',
  })
}
</script>
